/**
 * Atelier storefront behaviours.
 *
 * <s-scroll-header>: toggles [data-scrolled] once the page scrolls past a small
 * threshold, so the homepage's transparent header can gain its solid surface.
 *
 * <s-count-up data-value="35">: counts from 0 to data-value when it first enters
 * the viewport. The server-rendered number stays in place without JavaScript
 * and when the visitor prefers reduced motion.
 */

class SScrollHeader extends HTMLElement {
  #ac;
  #ticking = false;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#update();
    window.addEventListener('scroll', () => this.#schedule(), { passive: true, signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #schedule() {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#update();
    });
  }

  #update() {
    this.toggleAttribute('data-scrolled', window.scrollY > 24);
  }
}

class SCountUp extends HTMLElement {
  #observer;

  connectedCallback() {
    const target = Number(this.dataset.value);
    if (!Number.isFinite(target) || target <= 0) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#observer.disconnect();
      this.#run(target);
    }, { threshold: 0.6 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #run(target) {
    const duration = 1400;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.textContent = String(Math.round(target * eased));
      if (progress < 1) requestAnimationFrame(step);
    };
    this.textContent = '0';
    requestAnimationFrame(step);
  }
}

if (!customElements.get('s-scroll-header')) customElements.define('s-scroll-header', SScrollHeader);
if (!customElements.get('s-count-up')) customElements.define('s-count-up', SCountUp);
